/**
 * Tracks-list panel (ADR-041 D-5; REQ-UI-170 / -171 / -172 / -180) — the
 * dockable surface listing the live **object tracks**, one expandable row per
 * track, complementing the Map's track lines and head glyphs.
 *
 * # Data
 *
 * Reads the shared :func:`object-tracks/object-track-store` (presented tracks
 * — two or more members — newest head first **at the displayed resolution**,
 * ties in the order already on screen; see :file:`track-sort.ts` for why a
 * live list sorted by the exact head time rotated under a steady provider and
 * took the keep-in-view scroll with it). The panel mounts the global
 * recent-tracks poll (:func:`useRecentTracksFeed`, summaries); members ride
 * the Map's viewport backfill and the detections SSE bridge, and a row that
 * is expanded while it holds no members backfills its own extent through
 * :func:`useObjectTracksFeed` so the expansion never sits empty.
 *
 * # Row contract (R5 / R8)
 *
 * **Track row:** chevron · head time (HH:MM:SS) · class · ``×N`` · duration
 * (m:ss) · producer display name (REQ-UI-110 join) · an **"approx"** badge
 * when any member is unpositioned (REQ-UI-071 carried up) · the ``TRK-…``
 * handle. Left-click → ``inspect({kind: "track"})`` (the ``inspected`` slot
 * only); right-click / ``Shift+F10`` → the shared "Select on Map" menu
 * (``locatable`` = at least one positioned member).
 *
 * **Expansion** — the chevron, or ``→`` / ``←`` on the focused row — toggles
 * the track open **without changing selection**; ``aria-expanded`` rides the
 * row. Expanded, the **member detection rows** list newest first, indented:
 * time · class · confidence % · "approx". Each member row is a full
 * selection originator (click → ``inspect({kind: "detection"})``; right-click →
 * "Select on Map", which rings the point on the line). Expansion is
 * session-local — a ``Set<track_id>`` here, never in the Profile.
 *
 * **Selection-driven expansion (R4 / R8; REQ-UI-180).** Whenever either slot
 * holds a detection that belongs to a listed track, that track is expanded
 * (added — never collapsing others), its row takes a secondary highlight, and
 * the keep-in-view hook anchors the **member** row. This is what lands the
 * operator on the same detection inside its track after "Select on Map" on a
 * mid-track Detections-panel row or a map ring on a member. A detection whose
 * track is not live expands nothing.
 *
 * *Whenever* includes **at mount**, and the moment a held track first becomes
 * **presented**: a slot may already hold a member when this panel first
 * renders (it was closed when "Select on Map" ran, or the layout was restored
 * from a Profile with a detection inspected), and a one-member track has no
 * row to expand until its second member arrives. Both are the one keyed
 * render-time adjustment below.
 *
 * Cross-references:
 *   - REQ-UI-170 (this panel); REQ-UI-171 (Map ↔ Tracks selection);
 *     REQ-UI-172 (Select on Map fits the extent — the map layer's effect);
 *     REQ-UI-180 (selection-driven expansion); REQ-UI-181 (the live window in
 *     the empty state); REQ-UI-059 (keyboard + accessible names); REQ-UI-110.
 *   - ADR-041 D-5; ADR-024 §5 (the two-slot bus).
 *   - detections/detections-panel.tsx (the sibling this mirrors).
 */
import { memo, useCallback, useMemo, useRef, useState } from "react";
import {
  useInspectedIdForKind,
  useIsInspected,
  useMapSelectedIdForKind,
  useSelectionStore,
} from "@/selection/selection-store";
import { SelectOnMapContextMenu } from "@/selection/select-on-map-context-menu";
import {
  type PanelVisibilityApi,
  useKeepSelectedRowInView,
} from "@/selection/use-keep-selected-row-in-view";
import { useListFollow } from "@/multi-pane/list-follow-store";
import { ListFollowToggle } from "@/multi-pane/list-follow-toggle";
import { usePausedListRows } from "@/multi-pane/use-paused-list-rows";
import { useLiveWindowMs } from "@/chrome/use-deployment-config";
import { classificationLabel, formatConfidence } from "@/detections/detection-display";
import { usePreferences } from "@/preferences/preferences-context";
import { type ProducerNameIndex, useProducerNames } from "@/lib/use-producer-names";
import type { SortSelection } from "@/lib/list-sort";
import type { TrackListSort } from "@/auth/session-types";
import { shortId } from "@/lib/short-id";
import type { SnapshotViewport } from "@/map/snapshot-types";
import { useObjectTracksFeed } from "@/map/use-object-tracks-feed";
import {
  type ObjectTrackRecord,
  type TrackMember,
  useLiveObjectTracks,
  useTrackIdForDetection,
} from "@/object-tracks/object-track-store";
import { useRecentTracksFeed } from "@/object-tracks/use-recent-tracks-feed";
import { TRACK_DEFAULT_SORT, sortTrackRows, trackOrderIndex } from "@/object-tracks/track-sort";
import {
  formatDuration,
  formatMemberCount,
  formatTrackClock,
  memberWindow,
  summaryEnvelope,
  trackDurationSeconds,
  trackHasApproximateMember,
  trackIsLocatable,
  trackMemberCount,
} from "@/object-tracks/track-display";

/** The degenerate-extent pad (degrees) for a one-point track's own backfill box. */
const EXTENT_PAD_DEG = 0.0005;

/**
 * The sorted rows together with the inputs they were sorted from, so a render
 * can tell "same inputs, reuse" from "new inputs, sort again with the order
 * last rendered as the tie-break" (REQ-UI-170 amended 2026-10-08).
 */
interface SortedTracksState {
  readonly tracks: readonly ObjectTrackRecord[];
  readonly names: ProducerNameIndex;
  readonly selection: SortSelection<TrackListSort["field"]>;
  readonly sorted: readonly ObjectTrackRecord[];
}

interface TracksPanelProps {
  /** dockview panel api, threaded by :func:`TracksDockPanel` (show-to-scroll). */
  panelApi?: PanelVisibilityApi;
}

/** A track row's stable identity for the pause snapshot. */
const trackRowId = (track: ObjectTrackRecord): string => track.track_id;

export function TracksPanel({ panelApi }: TracksPanelProps = {}): React.ReactElement {
  const feed = useRecentTracksFeed();
  const tracks = useLiveObjectTracks();
  const names = useProducerNames();
  // Row and member-row left-clicks inspect (``inspected`` slot only); a second
  // left-click on the already-inspected row deselects — both slots clear, the
  // camera stays (REQ-UI-051 amended 2026-10-01). Expansion is independent.
  const toggleInspect = useSelectionStore((s) => s.toggleInspect);
  const liveWindowMs = useLiveWindowMs();
  const listRef = useRef<HTMLUListElement | null>(null);

  const selectedTrackId = useInspectedIdForKind("track");
  // The map slot too, only so a track selected on the map is admitted to a
  // paused list and therefore has a row to reveal (REQ-UI-051).
  const mapSelectedTrackId = useMapSelectedIdForKind("track");
  // Both slots may hold a member detection (a list click sets ``inspected``;
  // a map ring / "Select on Map" sets both) — either expands its track.
  const inspectedDetectionId = useInspectedIdForKind("detection");
  const mapDetectionId = useMapSelectedIdForKind("detection");
  const inspectedMemberTrackId = useTrackIdForDetection(inspectedDetectionId);
  const mapMemberTrackId = useTrackIdForDetection(mapDetectionId);

  const sortSelection = usePreferences().workingState.list_sort?.tracks ?? TRACK_DEFAULT_SORT;
  // Rows move only for a difference the operator can see (REQ-UI-170 amended
  // 2026-10-08): the sort compares keys at the displayed resolution and
  // resolves ties to the order already on screen. The previous order is the
  // previous *result*, so the sorted list is state keyed on its inputs (React's
  // "storing information from previous renders" pattern, the same one the
  // selection-driven expansion below uses) rather than a memo over a ref,
  // which a render may not read. Inputs unchanged → the stored result; inputs
  // changed → one sort, fed the order last rendered, and an immediate re-run.
  const [sortedState, setSortedState] = useState<SortedTracksState>(() => ({
    tracks,
    names,
    selection: sortSelection,
    sorted: sortTrackRows(tracks, names, sortSelection),
  }));
  if (
    sortedState.tracks !== tracks ||
    sortedState.names !== names ||
    sortedState.selection !== sortSelection
  ) {
    setSortedState({
      tracks,
      names,
      selection: sortSelection,
      sorted: sortTrackRows(tracks, names, sortSelection, trackOrderIndex(sortedState.sorted)),
    });
  }
  const sortedTracks = sortedState.sorted;

  // What the list renders. Following ⇒ the live sorted rows; not following ⇒
  // the rows frozen at the flip, because a re-sorting list cannot be held by
  // scrolling — the scrollbar travelled 2,227 → 5,502 px in 30 s on the
  // hermetic rig and any re-sort teleported the viewport (REQ-UI-182 amended
  // 2026-10-10; see ``use-paused-list-rows``). A track selected anywhere is
  // admitted so it still has a row while paused.
  const follow = useListFollow("tracks");
  const { rows: visibleTracks, withheld } = usePausedListRows({
    rows: sortedTracks,
    follow,
    idOf: trackRowId,
    admit: [selectedTrackId, mapSelectedTrackId, inspectedMemberTrackId, mapMemberTrackId],
  });
  // The ids the panel actually LISTS — the expansion key below reads this, so
  // it must be the rendered rows, not the live ones.
  const listedIds = useMemo(() => new Set(visibleTracks.map((t) => t.track_id)), [visibleTracks]);

  // Session-local expansion (REQ-UI-170): never persisted.
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  // The three row callbacks are stable per panel, not per render: the rows are
  // memoised (feature spec ``ui-track-load-headroom`` §2.2) and an inline arrow
  // per row would hand every row a fresh prop on every live event. Each takes
  // the row's id so one function serves every row; ``toggleExpanded`` uses the
  // functional updater so it needs no dependency.
  const toggleExpanded = useCallback((trackId: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  }, []);
  const onSelectTrack = useCallback(
    (trackId: string): void => toggleInspect({ kind: "track", id: trackId }),
    [toggleInspect],
  );
  const onSelectMember = useCallback(
    (reportId: string): void => toggleInspect({ kind: "detection", id: reportId }),
    [toggleInspect],
  );

  // Selection-driven expansion (REQ-UI-180): only ever ADDS to the set, and
  // stays open after the selection moves on. Applied as a render-time state
  // adjustment (React's "adjust state when a prop changes" pattern) rather
  // than an effect, so the expansion is in the same commit as the selection
  // and never a frame behind it.
  //
  // Two properties of the key are load-bearing (both fixed 2026-10-05):
  //
  //   - The applied key starts as ``null``, which no real key can equal, so
  //     the adjustment runs on the **first** render too. Seeded with the
  //     current key instead, a selection that already existed at mount never
  //     fired it and the owning track opened collapsed — "Select on Map" on a
  //     mid-track Detections row while this panel is closed and then opening
  //     it, or a layout restored from a Profile with a detection inspected.
  //   - The key is built from the ids the panel actually *lists*, not from the
  //     raw resolved ids, so a track that is held but not yet **presented**
  //     (one member — ADR-041 D-1, hence nothing to expand when the selection
  //     landed) expands when its second member arrives under an unchanged
  //     selection.
  //
  // It stays *keyed* rather than re-applied every render, which is what keeps
  // the expansion a one-shot add: the operator can still collapse a track
  // that holds the selection, with no standing constraint re-opening it.
  const toOpen = [inspectedMemberTrackId, mapMemberTrackId].filter(
    (id): id is string => id !== null && listedIds.has(id),
  );
  const selectionKey = toOpen.join("|");
  const [appliedSelectionKey, setAppliedSelectionKey] = useState<string | null>(null);
  if (selectionKey !== appliedSelectionKey) {
    setAppliedSelectionKey(selectionKey);
    if (toOpen.some((id) => !expanded.has(id))) {
      setExpanded(new Set([...expanded, ...toOpen]));
    }
  }

  // Keep the selected row in view. The anchored row is whichever carries
  // ``aria-selected``: the inspected member row when a listed track's
  // detection is inspected, else the inspected track row. Keyed on the sorted
  // list AND the expansion set so a just-expanded member row is measured.
  const memberAnchored = inspectedDetectionId !== null && inspectedMemberTrackId !== null;
  const anchoredId = memberAnchored ? inspectedDetectionId : selectedTrackId;
  const listRevision = useMemo(() => ({ visibleTracks, expanded }), [visibleTracks, expanded]);
  useKeepSelectedRowInView(listRef, anchoredId, listRevision, panelApi, { follow });

  if (feed.isLoading && tracks.length === 0) {
    return <LoadingState />;
  }
  if (feed.isError && tracks.length === 0) {
    return <ErrorState message={feed.error.message} onRetry={() => void feed.refetch()} />;
  }
  // Gated on the rendered rows: a list paused while empty stays empty.
  if (visibleTracks.length === 0) {
    return <EmptyState liveWindowMs={liveWindowMs} />;
  }

  return (
    <div
      data-testid="tracks-panel"
      role="region"
      aria-label="Live tracks"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="shrink-0 whitespace-nowrap text-body font-medium text-text-primary">
          Tracks <span className="ml-2 text-body-sm text-text-muted">{visibleTracks.length}</span>
        </h2>
        <ListFollowToggle panelId="tracks" withheld={withheld} />
      </header>
      <ul
        ref={listRef}
        // ``overscroll-contain``: a wheel that reaches this list's end must not
        // continue into dockview's own ``.dv-view`` — an ``overflow-y: auto``
        // wrapper around the whole group, tab strip included — which scrolls the
        // panel's tab up under the top nav bar while the page itself never moves
        // (measured on Selection Details 2026-10-10: ``.dv-view`` scrollTop
        // 0 → 605, group y 48 → -557). No list panel can reach that state today
        // (``.dv-view`` has no scrollable extent around one — probed in both
        // directions at two viewport heights), but the end of a list is now a
        // place operators sit: a paused list stops moving, so they scroll to the
        // bottom and keep going (REQ-UI-182 amended 2026-10-10).
        className="flex-1 overflow-y-auto overscroll-contain"
        role="list"
        aria-label="Track list"
      >
        {visibleTracks.map((track) => (
          <TrackRow
            key={track.track_id}
            track={track}
            platformName={names.nameFor(track.producer_id)}
            expanded={expanded.has(track.track_id)}
            memberSelected={
              (inspectedMemberTrackId === track.track_id && inspectedDetectionId !== null) ||
              (mapMemberTrackId === track.track_id && mapDetectionId !== null)
            }
            // Which of this track's members is selected, so the capped
            // expansion can keep it visible (REQ-UI-180 ⋂ REQ-UI-187).
            selectedMemberId={
              inspectedMemberTrackId === track.track_id
                ? inspectedDetectionId
                : mapMemberTrackId === track.track_id
                  ? mapDetectionId
                  : null
            }
            onToggle={toggleExpanded}
            onSelect={onSelectTrack}
            onSelectMember={onSelectMember}
          />
        ))}
      </ul>
    </div>
  );
}

interface TrackRowProps {
  track: ObjectTrackRecord;
  platformName: string;
  expanded: boolean;
  /** A member of this track is selected somewhere → the secondary highlight. */
  memberSelected: boolean;
  /**
   * The selected member's ``report_id``, or ``null``. Carried so the capped
   * expansion can keep it on screen — see :func:`memberWindow`.
   */
  selectedMemberId: string | null;
  /** Expand / collapse this row; receives the row's ``track_id``. */
  onToggle: (trackId: string) => void;
  /** Toggle ``inspected`` on this track; receives the row's ``track_id``. */
  onSelect: (trackId: string) => void;
  onSelectMember: (reportId: string) => void;
}

/** The backfill box for a track that is expanded but holds no members. */
function extentViewport(track: ObjectTrackRecord): SnapshotViewport | null {
  const env = track.summary !== null ? summaryEnvelope(track.summary) : null;
  if (env === null) {
    return null;
  }
  return {
    min_longitude: Math.max(-180, env.minLon - EXTENT_PAD_DEG),
    min_latitude: Math.max(-90, env.minLat - EXTENT_PAD_DEG),
    max_longitude: Math.min(180, env.maxLon + EXTENT_PAD_DEG),
    max_latitude: Math.min(90, env.maxLat + EXTENT_PAD_DEG),
  };
}

/**
 * One track row. Memoised (feature spec ``ui-track-load-headroom`` §2.2): a live
 * detection commits the store and re-renders the panel, but a row whose
 * ``track`` record and primitive props are unchanged must not re-render.
 * ``isSelected`` and the backfill hook are read inside, so selection and
 * expansion still re-render exactly the rows they touch.
 */
const TrackRow = memo(function TrackRow({
  track,
  platformName,
  expanded,
  memberSelected,
  selectedMemberId,
  onToggle,
  onSelect,
  onSelectMember,
}: TrackRowProps): React.ReactElement {
  const isSelected = useIsInspected("track", track.track_id);
  const count = trackMemberCount(track);
  const duration = formatDuration(trackDurationSeconds(track));
  const approximate = trackHasApproximateMember(track);
  const locatable = trackIsLocatable(track);
  const headTime =
    track.members.length > 0
      ? track.members[track.members.length - 1].time
      : (track.summary?.last_time ?? "");

  // An expanded row with no held members (the summary came off the global
  // poll; the map never backfilled this extent) fetches its own extent so the
  // expansion is populated — the store's ``seed`` unions the result in.
  useObjectTracksFeed({
    viewport: expanded && track.members.length === 0 ? extentViewport(track) : null,
  });

  // Newest first, like the Detections panel, and capped — the expansion is a
  // quick look, not the history (REQ-UI-187).
  const allMembers = useMemo(() => [...track.members].reverse(), [track.members]);
  const members = useMemo(
    () => memberWindow(allMembers, selectedMemberId),
    [allMembers, selectedMemberId],
  );
  // What the operator cannot see here. Counted off the track's own count
  // rather than off ``members.length``, so a track whose summary knows of
  // more detections than the live buffer holds still reports honestly.
  const hiddenCount = Math.max(0, count - members.length);

  const label =
    `track from ${platformName}, ${count} detections over ${duration}, ` +
    `head at ${formatTrackClock(headTime)}${approximate ? ", includes approximate positions" : ""}`;

  return (
    <SelectOnMapContextMenu value={{ kind: "track", id: track.track_id }} locatable={locatable}>
      <li
        tabIndex={0}
        role="listitem"
        aria-selected={isSelected}
        aria-expanded={expanded}
        aria-label={label}
        data-row-id={track.track_id}
        data-testid={`track-row-${track.track_id}`}
        data-member-selected={memberSelected ? "true" : undefined}
        onClick={() => onSelect(track.track_id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(track.track_id);
          } else if (e.key === "ArrowRight" && !expanded) {
            e.preventDefault();
            onToggle(track.track_id);
          } else if (e.key === "ArrowLeft" && expanded) {
            e.preventDefault();
            onToggle(track.track_id);
          }
        }}
        className={[
          "group relative cursor-pointer border-b border-border px-4 py-3",
          "transition-colors duration-100",
          "hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated",
          isSelected ? "bg-bg-elevated" : "",
        ].join(" ")}
      >
        {/* Left stripe: full brand when the track itself is inspected; a
         * half-strength stripe when one of its members is (the secondary
         * highlight, REQ-UI-180). */}
        <span
          aria-hidden="true"
          className={[
            "absolute left-0 top-0 h-full w-[3px] transition-colors duration-100",
            isSelected ? "bg-brand" : memberSelected ? "bg-brand/50" : "bg-transparent",
          ].join(" ")}
        />
        <div className="flex items-start gap-2">
          <button
            type="button"
            aria-label={expanded ? "Collapse track" : "Expand track"}
            aria-expanded={expanded}
            data-testid={`track-row-toggle-${track.track_id}`}
            onClick={(e) => {
              // The chevron changes expansion only — never the selection.
              e.stopPropagation();
              onToggle(track.track_id);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            className="mt-0.5 shrink-0 rounded px-1 font-mono text-body-sm text-text-muted hover:text-text-primary focus:outline-none focus-visible:text-text-primary"
          >
            {expanded ? "▾" : "▸"}
          </button>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <span
                className="font-mono text-body-sm text-text-secondary"
                data-testid="track-row-head-time"
              >
                {formatTrackClock(headTime)}
              </span>
              <span className="flex shrink-0 items-center gap-2 font-mono text-body-sm text-text-secondary">
                <span data-testid="track-row-count">{formatMemberCount(count)}</span>
                <span data-testid="track-row-duration">{duration}</span>
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              {/* The row names its kind, not the head's classification: every
                  simulator-fed row read "detection" and was taken for a kind
                  label (REQ-UI-170 amended 2026-10-01). The class stays on the
                  member rows below and in Selection Details. */}
              <span className="text-body text-text-primary" data-testid="track-row-kind">
                track
              </span>
              {approximate && (
                <span
                  data-testid="track-row-approximate"
                  aria-hidden="true"
                  title="One or more members carry no resolved location (range/bearing); they count and time the track but draw no line vertex (REQ-UI-071)."
                  className="inline-flex shrink-0 items-center rounded border border-border px-1.5 py-0.5 text-caption font-medium uppercase tracking-wide text-text-muted"
                >
                  approx
                </span>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <span
                className="truncate font-mono text-body-sm text-text-muted"
                data-testid="track-row-producer"
                title={track.producer_id}
              >
                {platformName}
              </span>
              <span
                className="shrink-0 font-mono text-caption text-text-muted"
                data-testid="track-row-id"
                aria-hidden="true"
                title={track.track_id}
              >
                {shortId(track.track_id, "TRK")}
              </span>
            </div>
          </div>
        </div>
        {expanded && (
          <ul
            role="list"
            aria-label={`Detections of track ${shortId(track.track_id, "TRK")}`}
            data-testid={`track-members-${track.track_id}`}
            className="mt-2 flex flex-col border-l border-border pl-3"
            onClick={(e) => {
              // Member clicks are their own selection; never bubble to the track.
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            {members.length === 0 && (
              <li className="py-1 text-body-sm text-text-muted" data-testid="track-members-empty">
                Loading detections…
              </li>
            )}
            {members.map((member) => (
              <MemberRow key={member.report_id} member={member} onSelect={onSelectMember} />
            ))}
            {hiddenCount > 0 && (
              <li className="pt-1">
                {/* Not a bare "…": an ellipsis does not say whether two or
                    twenty thousand are hidden, and it leaves the operator
                    nowhere to go. This names the count and selects the track,
                    which is the surface that pages the full list
                    (REQ-UI-187 / REQ-UI-174; CLAUDE.md §8.1). */}
                <button
                  type="button"
                  data-testid={`track-members-more-${track.track_id}`}
                  onClick={() => onSelect(track.track_id)}
                  className="rounded px-2 py-0.5 text-left text-body-sm text-text-muted hover:bg-bg-elevated hover:text-text-primary focus:outline-none focus-visible:bg-bg-elevated"
                >
                  … {hiddenCount.toLocaleString("en-US")} more — open the track for the full list
                </button>
              </li>
            )}
          </ul>
        )}
      </li>
    </SelectOnMapContextMenu>
  );
});

/** One expanded member row. Memoised for the same reason as :func:`TrackRow`. */
const MemberRow = memo(function MemberRow({
  member,
  onSelect,
}: {
  member: TrackMember;
  onSelect: (reportId: string) => void;
}): React.ReactElement {
  const isSelected = useIsInspected("detection", member.report_id);
  const classification = classificationLabel(member.primary_class);
  const confidence = formatConfidence(member.detection_confidence);
  const locatable = member.latitude_deg !== null && member.longitude_deg !== null;
  return (
    <SelectOnMapContextMenu
      value={{ kind: "detection", id: member.report_id }}
      locatable={locatable}
    >
      <li
        tabIndex={0}
        role="listitem"
        aria-selected={isSelected}
        aria-label={`${classification} detection at ${formatTrackClock(member.time)}, confidence ${confidence}${locatable ? "" : ", approximate position (range/bearing)"}`}
        data-testid={`track-member-row-${member.report_id}`}
        onClick={() => onSelect(member.report_id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(member.report_id);
          }
        }}
        className={[
          "relative flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1",
          "hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated",
          isSelected ? "bg-bg-elevated" : "",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute -left-[13px] top-0 h-full w-[3px]",
            isSelected ? "bg-brand" : "bg-transparent",
          ].join(" ")}
        />
        <span
          className="font-mono text-body-sm text-text-secondary"
          data-testid="track-member-time"
        >
          {formatTrackClock(member.time)}
        </span>
        <span
          className="min-w-0 flex-1 truncate text-body-sm text-text-primary"
          data-testid="track-member-class"
        >
          {classification}
        </span>
        {!locatable && (
          <span
            data-testid="track-member-approximate"
            aria-hidden="true"
            className="inline-flex shrink-0 items-center rounded border border-border px-1 py-0 text-caption font-medium uppercase tracking-wide text-text-muted"
          >
            approx
          </span>
        )}
        <span
          className="shrink-0 font-mono text-body-sm text-text-secondary"
          data-testid="track-member-confidence"
        >
          {confidence}
        </span>
      </li>
    </SelectOnMapContextMenu>
  );
});

// ---- Render-state stubs ------------------------------------------

function LoadingState(): React.ReactElement {
  return (
    <div
      data-testid="tracks-panel-loading"
      role="status"
      aria-label="Loading tracks"
      className="flex h-full w-full items-center justify-center bg-bg-base text-text-secondary"
    >
      <p className="text-body-sm">Loading tracks…</p>
    </div>
  );
}

function EmptyState({ liveWindowMs }: { liveWindowMs: number }): React.ReactElement {
  const minutes = Math.max(1, Math.round(liveWindowMs / 60_000));
  return (
    <div
      data-testid="tracks-panel-empty"
      role="region"
      aria-label="No tracks"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
    >
      {/* Storyboard Flow Y §Empty state — names the deployment live window (REQ-UI-181). */}
      <p className="text-body text-text-primary">
        No tracks in the last {minutes} {minutes === 1 ? "minute" : "minutes"}
      </p>
      <p className="text-body-sm text-text-muted">
        A track appears once a sensor reports the same object twice.
      </p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="tracks-panel-error"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-bg-base"
    >
      <p className="text-body text-destructive">Could not load tracks</p>
      <p className="px-6 text-center text-body-sm text-text-secondary">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded border border-border bg-bg-surface px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
      >
        Retry
      </button>
    </div>
  );
}
